// src/features/locations/UnsavedChangesModal.jsx
//
// LP1.6 — Three-action confirm modal that intercepts location nav while
// LocationDetail is in edit mode (LP1.3 Gate 2). Mirrors the L4 Gate 3
// Delete Location modal structure (inline overlay, no portal lib, fixed
// position, role=dialog + aria-modal, Escape closes, click-outside on
// the dim backdrop closes, initial focus on Cancel per the safety
// pattern for confirm dialogs).
//
// Contract: this component is purely presentational. The owner
// (LocationDetail) supplies all state — current/target labels, saving
// flag, save-error message — and the three handlers. The owner is
// responsible for resolving the pending-nav promise from each handler;
// this modal never touches the nav system itself.
//
// Error message strings come from LocationDetail's `errorToFormMessage`
// helper (the same pure helper feeds the page-level form-error banner
// during inline Save). One taxonomy, two render targets — page banner
// vs modal banner — so the message a user sees in the modal during
// Save matches the message they'd see if they'd clicked Save inline
// before navigating.

import React, { useEffect, useRef } from "react";
import { T } from "../../app/tokens.js";
import { S } from "../../app/styles.js";

export function UnsavedChangesModal({
  currentLocationName,
  targetLabel,
  saving,
  saveError,
  onCancel,
  onDiscard,
  onSave,
}) {
  const cancelBtnRef = useRef(null);

  // L4 Gate 3 safety pattern — initial focus lands on Cancel so a
  // keyboard user hitting Enter immediately does NOT discard their
  // draft or fire a Save. The "safe default" of a 3-action confirm
  // is the no-op (Cancel keeps current state).
  useEffect(() => {
    const id = requestAnimationFrame(() => cancelBtnRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  // Escape closes the modal (when not mid-flight). Mirrors the
  // L4 Gate 3 Delete confirm modal — `e.stopPropagation()` so the key
  // doesn't bubble to other Escape handlers (e.g. Stage/Health pill
  // dropdowns). Suppressed during Save in flight to prevent the user
  // from dismissing the modal while a PATCH is racing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, saving]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
      data-testid="unsaved-changes-modal-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-modal-title"
        data-testid="unsaved-changes-modal"
        style={{
          background: T.white,
          color: T.dark,
          borderRadius: 10,
          boxShadow: T.shadowModal,
          width: "100%", maxWidth: 480,
          padding: 20,
        }}
      >
        <h3
          id="unsaved-changes-modal-title"
          style={{ ...S.sectionTitle, marginTop: 0, marginBottom: 12 }}
        >
          Unsaved changes
        </h3>
        <p style={{ fontSize: T.fsBase5, color: T.dark, marginTop: 0, marginBottom: 16 }}>
          You have unsaved changes on {currentLocationName}. Save before continuing to {targetLabel}?
        </p>
        {saveError && (
          <div
            role="alert"
            data-testid="unsaved-changes-form-error"
            style={{
              background: T.utilRed,
              border: `1px solid ${T.red}`,
              color: T.red,
              padding: "8px 12px", borderRadius: 6,
              fontSize: T.fsSm5, marginBottom: 12,
            }}
          >{saveError}</div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            ref={cancelBtnRef}
            type="button"
            className="wos-btn"
            onClick={onCancel}
            disabled={saving}
            data-testid="unsaved-changes-cancel"
            style={{
              padding: "7px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold,
              color: T.dark, background: T.white,
              border: `1px solid ${T.lightGray}`, borderRadius: 6,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >Cancel</button>
          <button
            type="button"
            className="wos-btn"
            onClick={onDiscard}
            disabled={saving}
            data-testid="unsaved-changes-discard"
            style={{
              padding: "7px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold,
              color: T.white, background: T.red,
              border: "none", borderRadius: 6,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >Discard</button>
          <button
            type="button"
            className="wos-btn"
            onClick={onSave}
            disabled={saving}
            data-testid="unsaved-changes-save"
            style={{
              padding: "7px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold,
              color: T.white, background: T.blue,
              border: "none", borderRadius: 6,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? "Saving…" : "Save and continue"}</button>
        </div>
      </div>
    </div>
  );
}
